import { getDate, getTime } from '../../../helpers/utils'
import { IStudio } from '../../../pages/studios-container/share-type'
import { OptionType, PageInfo } from '../../../share-types'
import { ReduxStore } from '../../../store'
import { getStudioBookings, StudioBookingsQueryParams } from '../../../studios-area-api/bookingsApi'
import { BillingDetailDto } from '../billing-summaries/share-type'
import { Button, Card, DatePicker, Select, Space, Spin, Table, Tag } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { compose } from 'redux'

const defaultPage: PageInfo = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const bookingManagementFiltersStorageKey = 'studios-area-booking-management-filters'

const bookingStatusLabels: Record<string, string> = {
  NO_SHOW: '没到',
  EARLY_VISIT: '初访',
  LATE_CANCELLED: '延迟取消(12小时内)',
  CANCELLED: '取消',
  CHECKED_IN: '已签到',
  COMPLETED: '已完成',
  ATTENDANCE_NOT_RECORDED: '未签到',
}

type BookingFilters = {
  studioIds: number[]
  startDate: Dayjs
  endDate: Dayjs | null
}

type StoredBookingFilters = {
  studioIds: number[]
  autoRefreshSeconds: number
}

const getStoredBookingFilters = (): StoredBookingFilters | null => {
  try {
    const serializedFilters = window.localStorage.getItem(bookingManagementFiltersStorageKey)
    if (!serializedFilters) {
      return null
    }
    const filters = JSON.parse(serializedFilters) as StoredBookingFilters
    const validAutoRefreshSeconds = [0, 30, 60, 300]
    if (
      !Array.isArray(filters.studioIds) ||
      !validAutoRefreshSeconds.includes(filters.autoRefreshSeconds)
    ) {
      return null
    }
    return filters
  } catch {
    return null
  }
}

const BookingManagement = (props: ConnectedProps<typeof withConnect>) => {
  const { studioAdminUserInfo } = props
  const [studiosOptions, setStudiosOptions] = useState<OptionType[]>([])
  const [selectedStudioIds, setSelectedStudioIds] = useState<number[]>([])
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().tz('Asia/Shanghai').startOf('day'))
  const [endDate, setEndDate] = useState<Dayjs | null>(null)
  const [bookings, setBookings] = useState<BillingDetailDto[]>([])
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  const fetchBookings = useCallback(
    (page: number, size: number, filters?: BookingFilters) => {
      if (!studioAdminUserInfo) {
        return
      }
      setLoading(true)
      const queryFilters = filters ?? { studioIds: selectedStudioIds, startDate, endDate }
      const params: StudioBookingsQueryParams = {
        studioIds: queryFilters.studioIds,
        startDate: queryFilters.startDate.toISOString(),
        page: page - 1,
        size,
      }
      if (queryFilters.endDate) {
        params.endDate = queryFilters.endDate.endOf('day').toISOString()
      }
      getStudioBookings(params)
        .then(response => {
          if (response) {
            setBookings(response.data)
            setPageInfo({ ...response.pageInfo, currentPage: response.pageInfo.currentPage + 1 })
          }
        })
        .finally(() => setLoading(false))
    },
    [endDate, selectedStudioIds, startDate, studioAdminUserInfo],
  )

  useEffect(() => {
    if (!studioAdminUserInfo?.studios) {
      return
    }
    const options: OptionType[] = studioAdminUserInfo.studios.map((studio: IStudio) => {
      const translation = studio.studioTranslations.find(item => item.locale === 'zh-CN')
      const studioName = translation?.name ?? studio.studioTranslations[0]?.name ?? ''
      return { label: `${studio.id}-${studioName}`, value: studio.id }
    })
    const storedFilters = getStoredBookingFilters()
    const storedStudioIds = storedFilters?.studioIds.filter(studioId =>
      options.some(option => option.value === studioId),
    )
    const initialFilters: BookingFilters = {
      studioIds:
        storedFilters && (storedFilters.studioIds.length === 0 || storedStudioIds?.length)
          ? (storedStudioIds ?? [])
          : options.length > 0
            ? [options[0].value]
            : [],
      startDate: dayjs().tz('Asia/Shanghai').startOf('day'),
      endDate: null,
    }
    setStudiosOptions(options)
    setSelectedStudioIds(initialFilters.studioIds)
    setStartDate(initialFilters.startDate)
    setEndDate(initialFilters.endDate)
    setAutoRefreshSeconds(storedFilters?.autoRefreshSeconds ?? 0)
    setIsInitialized(true)
    fetchBookings(defaultPage.currentPage, defaultPage.pageSize, initialFilters)
  }, [studioAdminUserInfo])

  useEffect(() => {
    if (!isInitialized) {
      return
    }
    const filters: StoredBookingFilters = {
      studioIds: selectedStudioIds,
      autoRefreshSeconds,
    }
    window.localStorage.setItem(bookingManagementFiltersStorageKey, JSON.stringify(filters))
  }, [autoRefreshSeconds, isInitialized, selectedStudioIds])

  useEffect(() => {
    if (!autoRefreshSeconds) {
      return
    }
    const timer = window.setInterval(() => {
      fetchBookings(pageInfo.currentPage, pageInfo.pageSize)
    }, autoRefreshSeconds * 1000)
    return () => window.clearInterval(timer)
  }, [autoRefreshSeconds, fetchBookings, pageInfo.currentPage, pageInfo.pageSize])

  const resetConditions = () => {
    const firstStudioId = studiosOptions[0]?.value
    const initialFilters = {
      studioIds: firstStudioId ? [firstStudioId] : [],
      startDate: dayjs().tz('Asia/Shanghai').startOf('day'),
      endDate: null,
    }
    setSelectedStudioIds(initialFilters.studioIds)
    setStartDate(initialFilters.startDate)
    setEndDate(initialFilters.endDate)
    setAutoRefreshSeconds(0)
    setPageInfo(defaultPage)
    fetchBookings(defaultPage.currentPage, defaultPage.pageSize, initialFilters)
  }

  const columns = [
    { title: '预约ID', dataIndex: 'bookingId', key: 'bookingId' },
    { title: '课程名称', dataIndex: 'lessonName', key: 'lessonName' },
    {
      title: '课程日期',
      dataIndex: 'lessonStartTime',
      key: 'lessonStartDate',
      render: (lessonStartTime: string) => getDate(lessonStartTime),
    },
    {
      title: '课程时间',
      dataIndex: 'lessonStartTime',
      key: 'lessonStartTime',
      render: (lessonStartTime: string) => getTime(lessonStartTime),
    },
    { title: '预约用户', dataIndex: 'bookingUser', key: 'bookingUser' },
    { title: '用户电话', dataIndex: 'userPhoneNumber', key: 'userPhoneNumber' },
    { title: '工作室名称', dataIndex: 'studioName', key: 'studioName' },
    { title: '预约码', dataIndex: 'bookingRef', key: 'bookingRef' },
    {
      title: '状态',
      dataIndex: 'type',
      key: 'type',
      render: (type: string | null, booking: BillingDetailDto) => {
        const label = booking.pending ? '待处理' : ((type && bookingStatusLabels[type]) ?? '已预约')
        return <Tag>{label}</Tag>
      },
    },
    { title: '结算金额', dataIndex: 'gpRate', key: 'gpRate' },
  ]

  return (
    <Space style={{ width: '100%' }} orientation="vertical" size={18}>
      <div className="studios-area-page-title">预约管理</div>
      <Spin spinning={loading}>
        <Card className="studios-area-card" bordered={false}>
          <Space size={12} wrap>
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 240 }}
              options={studiosOptions}
              value={selectedStudioIds}
              onChange={setSelectedStudioIds}
              placeholder="全部工作室"
            />
            <DatePicker
              value={startDate}
              allowClear={false}
              onChange={value => value && setStartDate(value)}
            />
            <span>至</span>
            <DatePicker value={endDate} onChange={setEndDate} placeholder="未来全部" />
            <Button
              type="primary"
              loading={loading}
              onClick={() => fetchBookings(defaultPage.currentPage, defaultPage.pageSize)}
            >
              查询
            </Button>
            <Button onClick={resetConditions}>重置</Button>
            <Select
              style={{ width: 140 }}
              options={[
                { label: '不自动刷新', value: 0 },
                { label: '30秒自动更新', value: 30 },
                { label: '1分钟自动更新', value: 60 },
                { label: '5分钟自动更新', value: 300 },
              ]}
              value={autoRefreshSeconds}
              onChange={setAutoRefreshSeconds}
              placeholder="自动更新"
            />
          </Space>
          <div style={{ marginTop: 12, color: '#8c8c8c' }}>
            结束日期留空时，查询开始日期之后的全部预约。未核销预约按完成课价显示预计结算金额，实际金额以账单为准。
          </div>
          <Table
            style={{ marginTop: 20 }}
            rowKey={record => record.bookingId}
            columns={columns}
            dataSource={bookings}
            pagination={{
              current: pageInfo.currentPage,
              pageSize: pageInfo.pageSize,
              total: pageInfo.count,
              onChange: fetchBookings,
            }}
          />
        </Card>
      </Spin>
    </Space>
  )
}

const mapStateToProps = (state: ReduxStore) => ({
  studioAdminUserInfo: state.studioAdminUserInfo,
})

const withConnect = connect(mapStateToProps)

export default compose(withConnect)(BookingManagement)
